import * as API from '@pexip/infinity-api';
import type {Detach} from '@pexip/signal';
import {getErrorCode} from '@pexip/infinity-api';

import type {
    BreakoutRoom,
    CallSignals,
    ClientProps,
    ConferenceStatus,
    CurrentServiceType,
    ExtendedInfinityErrorMessage,
    GetEndpointParams,
    InfinityClient,
    InfinitySignals,
    NormalizedPresentationEvent,
    ParticipantID,
    RoomID,
    ThemeSchema,
} from './types';

import {
    createEventSourceManager,
    createEventSource,
    eventSignals,
} from './eventSource';
import {logger} from './logger';
import {isParticipantID, isRoomID} from './typeGuards';
import {MAIN_ROOM_ID} from './constants';
import {fetchWithInfoLog} from './fetcher';
import {createAuth} from './auth';
import {createEndpointManager} from './endpoint';
import {createCallManager} from './callManager';
import {createConferenceActions} from './actions/conferenceActions';
import {createBreakoutsActions} from './actions/breakoutsActions';
import {createParticipantsActions} from './actions/participantsActions';
import {createParticipants} from './participants';
import {createEventHandler} from './eventHandler';
import {createSerialQueue} from './utils';

interface Cache {
    rooms: RoomID[];
    breakoutRoomNames: Map<RoomID, string>;
}

const getDefaultProps = () => ({
    isDirectMedia: false,
    host: '',
    conferenceAlias: '',
    participantUuid: '' as ParticipantID,
    breakoutUuid: undefined,
    chatEnabled: false,
    guestsCanPresent: false,
    breakoutRoomsEnabled: false,
    serviceType: undefined,
});

/**
 * Creates the main Infinity client orchestrator.
 *
 * Coordinates authentication, call lifecycle, conference/breakout management,
 * participant actions, and event handling.
 *
 * @param signals - Outbound signals emitted by the client
 * @param callSignals - WebRTC call-specific signals
 */
export const createInfinityClient = (
    signals: InfinitySignals,
    callSignals: CallSignals,
): InfinityClient => {
    // ── State ────────────────────────────────────────────────────────────
    const props: ClientProps = getDefaultProps();
    let eventSourceManager:
        ReturnType<typeof createEventSourceManager> | undefined;
    let detachSignals: Detach[] = [];
    let participants = createParticipants();
    let conferenceStatus = new Map<RoomID, ConferenceStatus>();
    let themeSchema: ThemeSchema | undefined;
    let breakoutRooms = new Map<RoomID, BreakoutRoom>();
    const presentationCache = new Map<RoomID, NormalizedPresentationEvent>();
    const participantSync = new Map<RoomID, boolean>();
    const lifecycleQueue = createSerialQueue();
    let cache: Partial<Cache> = {};

    // ── Internal helpers ─────────────────────────────────────────────────

    /** Resolves the participant UUID assigned to a breakout room. */
    const getBreakoutParticipantUuid = (breakoutUuid: string | undefined) => {
        if (isRoomID(breakoutUuid)) {
            return breakoutRooms.get(breakoutUuid)?.participant_uuid;
        }
    };

    /** Returns the active room — breakout if joined, otherwise main. */
    const getCurrentRoomId = () => {
        return props.breakoutUuid || MAIN_ROOM_ID;
    };

    /**
     * Resolves the current user's participant record.
     *
     * In the main room, looks up by `participantUuid`.
     * In a breakout room, resolves the breakout-specific participant UUID first.
     */
    const getMe = (roomId?: string) => {
        if (roomId === MAIN_ROOM_ID) {
            return participants.get(roomId, props.participantUuid);
        }
        const breakoutParticipantUuid = getBreakoutParticipantUuid(
            roomId ?? props.breakoutUuid,
        );
        return participants.get(
            props.breakoutUuid || MAIN_ROOM_ID,
            breakoutParticipantUuid
                ? breakoutParticipantUuid
                : props.participantUuid,
        );
    };

    /**
     * Checks whether the remote participant in a gateway call supports FECC.
     *
     * Only meaningful for gateway calls with exactly 2 participants.
     */
    const gatewayParticipantCanFecc = () => {
        if (
            props.serviceType !== 'gateway' &&
            participants.get(getCurrentRoomId())?.size !== 2
        ) {
            return false;
        }
        return Boolean(
            participants.find(
                getCurrentRoomId(),
                participant => participant.uuid !== props.participantUuid,
            )?.canFecc,
        );
    };

    const setServiceType = (serviceType: CurrentServiceType) => {
        props.serviceType = serviceType;
        signals.onServiceType.emit(props.serviceType);
    };

    // ── Cleanup ───────────────────────────────────────────────────────────

    /** Detaches all signal subscriptions and cancels pending signal emissions. */
    const cleanupSignals = () => {
        for (const d of detachSignals) {
            d();
        }
        detachSignals = [];
        signals.cancel();
    };

    /** Tears down all state, sub-managers, and signal subscriptions. */
    const cleanup = () => {
        logger.debug({roomID: getCurrentRoomId()}, 'Cleanup');
        cache = {};
        eventSourceManager?.close();
        eventSourceManager = undefined;
        calls.teardownCall();
        participants = createParticipants();
        breakoutRooms = new Map();
        conferenceStatus = new Map();
        Object.assign(props, getDefaultProps());
        themeSchema = undefined;
        lifecycleQueue.reset();
        presentationCache.clear();
        participantSync.clear();

        auth.cleanup();
        calls.cleanup();
        endpoint.cleanup();

        cleanupSignals();
    };

    /**
     * Emits a disconnect signal, performs full cleanup, and releases the token.
     *
     * Called by sub-managers when a fatal error occurs (e.g. EventSource failure,
     * token expiry) that requires tearing down the entire session.
     */
    const cleanupAndDisconnect = (error: ExtendedInfinityErrorMessage) => {
        signals.onDisconnected.emit({
            error,
            errorCode: getErrorCode(error), // Match API error or get a default errorCode
        });
        cleanup();
        void auth.releaseToken(error).catch(e => {
            logger.error({error: e}, 'Failed to release token');
        });
    };

    // ── Sub-managers ─────────────────────────────────────────────────────
    const auth = createAuth({API, signals, cleanupAndDisconnect});
    const {generateEndpoint, ...endpoint} = createEndpointManager({
        cleanupAndDisconnect,
        signals,
        getRequestClient: () => auth.requestClient,
    });
    const calls = createCallManager({
        clientProps: props,
        generateEndpoint,
        eventSignals,
        signals,
        callSignals,
        API,
        getRequestClient: () => auth.requestClient,
        getEventSourceManager: () => eventSourceManager,
        getBreakoutParticipantUuid,
    });
    const conference = createConferenceActions({
        API,
        clientProps: props,
        generateEndpoint,
        getMe,
    });
    const breakouts = createBreakoutsActions({
        clientProps: props,
        API,
        generateEndpoint,
    });
    const participantsActions = createParticipantsActions({
        API,
        signals,
        clientProps: props,
        generateEndpoint,
        getMe,
        getBreakoutParticipantUuid,
        getCall: () => calls.call,
        getCallUuid: () => calls.callUuid,
        getParticipants: () => participants,
    });
    const eventHandler = createEventHandler({
        signals,
        eventSignals,
        callSignals,
        props,
        getCall: () => calls.call,
        getCallUuid: () => calls.callUuid,
        teardownCall: () => calls.teardownCall(),
        getParticipants: () => participants,
        getConferenceStatus: () => conferenceStatus,
        getBreakoutRooms: () => breakoutRooms,
        participantSync,
        presentationCache,
        getCurrentRoomId,
        getCurrentCallTag: () => auth.callTag,
        getThemeSchema: () => themeSchema,
        getBreakoutParticipantUuid,
        getRequestClientToken: () => auth.requestClient?.token,
        cleanupAndDisconnect,
        setServiceType,
        refreshToken: () => {
            void auth.requestClient?.refreshToken();
        },
        invalidateCache: key => {
            cache[key] = undefined;
        },
    });

    // ── Lifecycle internals ──────────────────────────────────────────────

    /**
     * Sets up a newly authenticated conference session.
     *
     * Configures conference props from the token response, fetches the theme,
     * establishes the EventSource connection, subscribes to signals, and starts
     * the WebRTC call.
     */
    const initializeConference = async ({
        allowChangeOfMediaDirection,
        allowH264,
        allowVP9,
        bandwidth,
        callType,
        conferenceAlias,
        degradationPreference,
        encodingPriorities,
        host,
        maxFrameSize,
        mediaStream,
        networkPriorities,
        result,
    }: Pick<
        GetEndpointParams<'call'>[0],
        | 'bandwidth'
        | 'mediaStream'
        | 'allowChangeOfMediaDirection'
        | 'networkPriorities'
        | 'encodingPriorities'
        | 'degradationPreference'
        | 'maxFrameSize'
        | 'callType'
    > & {
        result: API.RequestTokenMap['200']['result'];
        conferenceAlias: string;
        host: string;
        allowH264: boolean;
        allowVP9: boolean;
    }) => {
        props.isDirectMedia = Boolean(result.direct_media);
        props.chatEnabled = Boolean(result.chat_enabled);
        props.guestsCanPresent = Boolean(result.guests_can_present);
        props.breakoutRoomsEnabled = Boolean(result.breakout_rooms);
        props.conferenceAlias = conferenceAlias;
        if (isParticipantID(result.participant_uuid)) {
            props.participantUuid = result.participant_uuid;
        }
        props.host = host;

        setServiceType(result.current_service_type);

        if (props.isDirectMedia) {
            themeSchema = await conference.requestTheme({});
        }
        if (detachSignals.length > 0) {
            cleanupSignals();
        }
        eventSourceManager = createEventSourceManager({
            getToken: () => auth.requestClient?.token,
            createEventSource: token =>
                createEventSource({
                    host,
                    conferenceAlias,
                    token,
                }),
            onConnected: () => eventSignals.onConnected.emit(),
            onMaxReconnectAttemptsReached: () => eventSignals.onError.emit(),
        });
        await eventSourceManager.connect();
        detachSignals = [
            ...eventHandler.subscribe(),
            ...calls.subscribeCallSignals(),
        ];

        calls.setPushStatisticsTimer();

        // Signal Subscriptions are deferred until the EventSource is connected
        // Thus we need to mimic the `onConnected` event manually
        signals.onConnected.emit();
        signals.onAuthenticatedWithConference.emit({
            conferenceAlias: props.conferenceAlias,
            conferenceName: auth.conferenceName,
        });
        logger.debug({currentCall: calls.call}, 'Creates a new call');
        calls.startCall({
            allow1080p: Boolean(result.allow_1080p),
            allowChangeOfMediaDirection,
            allowVP9: Boolean(result.vp9_enabled),
            bandwidth,
            bandwidthIn: result.bandwidth_in,
            bandwidthOut: result.bandwidth_out,
            clientAllowH264: allowH264,
            clientAllowVP9: allowVP9,
            clientCallType: callType,
            dataChannelId: result.pex_datachannel_id,
            degradationPreference,
            encodingPriorities,
            infinityCallType: result.call_type ?? 'video',
            maxFrameSize,
            mediaStream,
            networkPriorities,
            stun: result.stun,
            turn: result.turn,
        });
    };

    // ── Endpoint wrappers ────────────────────────────────────────────────

    /** Requests a token and initializes the conference on success. */
    const callRequest = generateEndpoint({
        func: async ([params]: GetEndpointParams<'call'>) => {
            const {
                bandwidth,
                conferenceAlias,
                directMedia = true,
                displayName,
                node = window.location.host,
                host = `${window.location.protocol}//${node}`,
                mediaStream,
                pin,
                chosenIdp,
                ssoToken,
                token,
                registrationToken,
                conferenceExtension,
                callTag,
                allowChangeOfMediaDirection,
                callType,
                clientId,
                networkPriorities,
                encodingPriorities,
                degradationPreference,
                supportsDirectChat = false,
                allowH264 = true,
                isMeetingManager = false,
                allowVP9 = true,
                maxFrameSize,
            } = params;

            const res = await auth.requestToken({
                pin,
                chosenIdp,
                ssoToken,
                token,
                registrationToken,
                conferenceExtension,
                callTag,
                conferenceAlias,
                displayName,
                node,
                host,
                clientId,
                supportsDirectChat,
                directMedia,
                callType,
                isMeetingManager,
            });

            if (res.status === 200) {
                await initializeConference({
                    allowChangeOfMediaDirection,
                    allowH264,
                    allowVP9,
                    bandwidth,
                    callType,
                    conferenceAlias,
                    degradationPreference,
                    encodingPriorities,
                    host,
                    maxFrameSize,
                    mediaStream,
                    networkPriorities,
                    result: res.data.result,
                });
            }
            return res;
        },
        funcName: 'call',
        fetcher: fetchWithInfoLog,
        requiresToken: false,
    });

    /** Notifies the server, performs cleanup, and releases the token. */
    const disconnectRequest = generateEndpoint({
        func: async ([params]: GetEndpointParams<'disconnect'>) => {
            const {
                callUuid = calls.callUuid,
                conferenceAlias = props.conferenceAlias,
                breakoutUuid = props.breakoutUuid,
                host = props.host,
                participantUuid = props.participantUuid,
                reason,
            } = params;

            // NOTE: we want to stop EventSource as soon as possible
            // to avoid processing any events during the disconnection process
            cleanup();

            await calls.disconnectCall({
                callUuid,
                conferenceAlias,
                breakoutUuid,
                breakoutParticipantUuid:
                    getBreakoutParticipantUuid(breakoutUuid),
                host,
                participantUuid,
                reason,
            });

            await auth.releaseToken(reason);
        },
        funcName: 'disconnect',
        fetcher: fetchWithInfoLog,
        requiresToken: true,
    });

    // ── Private lifecycle implementations ────────────────────────────────

    /**
     * Switches to a breakout room (or back to main).
     *
     * Preserves media stream from the current call. Bandwidth is
     * retained automatically by the call manager. Resumes any active
     * presentation in the target room.
     */
    const _joinBreakoutRoom = async ({
        breakoutUuid,
        mediaStream: mediaStreamOpt,
        ...opt
    }: {
        breakoutUuid?: string;
        mediaStream?: MediaStream | (() => MediaStream | undefined);
    }) => {
        const mediaStream = mediaStreamOpt ?? calls.call?.mediaStream;

        await calls.stopPresenting();
        await calls.disconnect();

        if (isRoomID(breakoutUuid) && breakoutUuid !== MAIN_ROOM_ID) {
            props.breakoutUuid = breakoutUuid;
        } else {
            props.breakoutUuid = undefined;
        }
        calls.startCall({
            ...opt,
            mediaStream,
        });
        calls.setPushStatisticsTimer();
        // Propagate presentation event if room has a running presentation
        // We assume that we join main room if props.breakoutUuid is undefined
        const presentationEvent = presentationCache.get(getCurrentRoomId());
        if (presentationEvent) {
            await calls.call?.receivePresentation(presentationEvent);
        }
    };

    /** Disconnects the current call and starts a new one with updated options. */
    const _restartCall = async (
        opt: Parameters<InfinityClient['restartCall']>[0],
    ) => {
        // NOTE: I believe we need to have some proper state where we are in the call lifecycle
        // then it could allow us to prevent calling restartCall after the cleanup
        if (!eventSourceManager) {
            throw new Error(
                'Cannot restart call. Connection is not established.',
            );
        }
        await calls.stopPresenting();
        await calls.disconnect();
        calls.startCall(opt);
        calls.setPushStatisticsTimer();
    };

    // ── Public serialized API ────────────────────────────────────────────

    const call = (...params: Parameters<typeof callRequest>) =>
        lifecycleQueue.enqueue(() => callRequest(...params));

    const disconnect = (...params: Parameters<typeof disconnectRequest>) =>
        lifecycleQueue.enqueue(() => disconnectRequest(...params));

    const joinBreakoutRoom = (
        ...params: Parameters<typeof _joinBreakoutRoom>
    ) => lifecycleQueue.enqueue(() => _joinBreakoutRoom(...params));

    const restartCall = (...params: Parameters<typeof _restartCall>) =>
        lifecycleQueue.enqueue(() => _restartCall(...params));

    // ── Return ───────────────────────────────────────────────────────────

    return {
        // Getters — state
        get breakoutRoomNames() {
            if (cache.breakoutRoomNames) {
                return cache.breakoutRoomNames;
            }
            const names = new Map<RoomID, string>();
            for (const roomId of breakoutRooms.keys()) {
                const status = conferenceStatus.get(roomId);
                if (status?.breakoutName) {
                    names.set(roomId, status.breakoutName);
                }
            }
            cache.breakoutRoomNames = names;
            return names;
        },
        get breakoutRooms() {
            return breakoutRooms;
        },
        get conferenceFeatureFlags() {
            return {
                isDirectMedia: props.isDirectMedia,
                chatEnabled: props.chatEnabled,
                guestsCanPresent: props.guestsCanPresent,
                breakoutRoomsEnabled: props.breakoutRoomsEnabled,
                callType: calls.infinityCallType,
            };
        },
        get conferenceName() {
            return auth.conferenceName;
        },
        get conferenceStatus() {
            return conferenceStatus;
        },
        get isFeccEnabled() {
            return gatewayParticipantCanFecc();
        },
        get latestStats() {
            return calls.latestStats;
        },
        get localCallType() {
            return calls.clientCallType;
        },
        get roomId() {
            return props.breakoutUuid ?? MAIN_ROOM_ID;
        },
        get rooms() {
            if (cache.rooms) {
                return cache.rooms;
            }
            const rooms = [MAIN_ROOM_ID];
            for (const room of breakoutRooms.keys()) {
                rooms.push(room);
            }
            cache.rooms = rooms;
            return rooms;
        },
        get secureCheckCode() {
            return calls.secureCheckCode;
        },
        get serviceType() {
            return props.serviceType;
        },

        // Methods — local
        getMe,
        getParticipants(roomID) {
            return participants.getAll(roomID);
        },
        isBreakoutRoom(roomId) {
            return Boolean(conferenceStatus.get(roomId)?.breakout);
        },

        // Auth
        setCallTag: auth.setCallTag,
        setConferenceExtension: auth.setConferenceExtension,
        setPin: auth.setPin,
        setSsoToken: auth.setSsoToken,
        setToken: auth.setToken,

        // Breakouts
        breakout: breakouts.breakout,
        breakoutAskForHelp: breakouts.breakoutAskForHelp,
        breakoutMoveParticipants: breakouts.breakoutMoveParticipants,
        breakoutRemoveAskForHelp: breakouts.breakoutRemoveAskForHelp,
        breakoutRequestGuestToken: breakouts.breakoutRequestGuestToken,
        closeBreakoutRoom: breakouts.closeBreakoutRoom,
        closeBreakouts: breakouts.closeBreakouts,
        emptyBreakouts: breakouts.emptyBreakouts,
        guestLeaveBreakoutRoom: breakouts.guestLeaveBreakoutRoom,

        // Calls
        present: calls.present,
        setBandwidth: calls.setBandwidth,
        setDegradationPreference: calls.setDegradationPreference,
        setFeccSupported(canFecc) {
            calls.setFeccSupported(canFecc);
        },
        setStream: calls.setStream,
        stopPresenting: () => {
            void calls.stopPresenting();
        },
        pushStatistics: calls.pushStatistics,

        // Conference
        availableLayouts: conference.availableLayouts,
        deleteVideoMix: conference.deleteVideoMix,
        dial: conference.dial,
        disconnectAll: conference.disconnectAll,
        guestsCanUnmute: conference.guestsCanUnmute,
        layoutSvgs: conference.layoutSvgs,
        liveCaptionsLanguages: conference.liveCaptionsLanguages,
        lock: conference.lock,
        lowerAllRaisedHands: conference.lowerAllRaisedHands,
        muteAllGuests: conference.muteAllGuests,
        requestParticipants: conference.requestParticipants,
        sendConferenceRequest: conference.sendConferenceRequest,
        setLayout: conference.setLayout,
        setPersonalLayout: conference.setPersonalLayout,
        setTextOverlay: conference.setTextOverlay,
        startConference: conference.startConference,

        // Participants
        admit: participantsActions.admit,
        clientMute: participantsActions.clientMute,
        fecc: participantsActions.fecc,
        kick: participantsActions.kick,
        liveCaptions: participantsActions.liveCaptions,
        mute: participantsActions.mute,
        muteVideo: participantsActions.muteVideo,
        notifyNotAFK: participantsActions.notifyNotAFK,
        presInMix: participantsActions.presInMix,
        raiseHand: participantsActions.raiseHand,
        requestAspectRatio: participantsActions.requestAspectRatio,
        sendApplicationMessage: participantsActions.sendApplicationMessage,
        sendDTMF: participantsActions.sendDTMF,
        sendMessage: participantsActions.sendMessage,
        setRole: participantsActions.setRole,
        spotlight: participantsActions.spotlight,
        transfer: participantsActions.transfer,

        // Lifecycle
        call,
        disconnect,
        joinBreakoutRoom,
        restartCall,
    };
};
